import { DOCUMENT } from '@angular/common';
import {
  Injectable,
  OnDestroy,
  Signal,
  WritableSignal,
  inject,
  signal
} from '@angular/core';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { Subscription, interval } from 'rxjs';
import { environment } from '../../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AppUpdaterService implements OnDestroy {
  private readonly swUpdate = inject(SwUpdate);
  private readonly document = inject(DOCUMENT);
  private readonly hasNewVersionState: WritableSignal<boolean> = signal(false);
  private readonly updateDialogVisibleState: WritableSignal<boolean> = signal(false);
  private readonly subscriptions = new Subscription();

  readonly hasNewVersion: Signal<boolean> = this.hasNewVersionState.asReadonly();
  readonly updateDialogVisible: Signal<boolean> = this.updateDialogVisibleState.asReadonly();

  constructor() {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.subscribeToVersionUpdates();
    this.subscribeToUnrecoverableState();
    this.startUpdateChecks();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  async installUpdate(): Promise<void> {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    try {
      const activated: boolean = await this.swUpdate.activateUpdate();

      if (activated) {
        this.reloadPage();
      }
    } catch {
      // Keep the current application running so the user can retry the update later.
    }
  }

  closeDialog(): void {
    this.updateDialogVisibleState.set(false);
  }

  private subscribeToVersionUpdates(): void {
    this.subscriptions.add(
      this.swUpdate.versionUpdates.subscribe((event: VersionEvent): void => {
        if (event.type !== 'VERSION_READY') {
          return;
        }

        this.hasNewVersionState.set(true);
        this.updateDialogVisibleState.set(true);
      })
    );
  }

  private subscribeToUnrecoverableState(): void {
    this.subscriptions.add(
      this.swUpdate.unrecoverable.subscribe((): void => {
        this.reloadPage();
      })
    );
  }

  private startUpdateChecks(): void {
    void this.checkForUpdate();

    const updateCheckInterval: unknown = environment.updateCheckInterval;

    if (
      typeof updateCheckInterval !== 'number' ||
      !Number.isFinite(updateCheckInterval) ||
      updateCheckInterval <= 0
    ) {
      return;
    }

    this.subscriptions.add(
      interval(updateCheckInterval).subscribe((): void => {
        void this.checkForUpdate();
      })
    );
  }

  private async checkForUpdate(): Promise<void> {
    try {
      await this.swUpdate.checkForUpdate();
    } catch {
      // A later interval can retry without interrupting the active application.
    }
  }

  private reloadPage(): void {
    this.document.defaultView?.location.reload();
  }
}
